import React, { useState, useRef, useEffect, CSSProperties, FCWithChildren } from 'react';
import Icon from '@components/atoms/icon/icon';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import useWindowSize from '@hooks/use-window-size';
import clsx from 'clsx';
import styles from './inline-select.module.scss';

interface InlineSelectPropsInterface {
  selectedItemLabel: string;
  containerPosition?: 'static' | 'absolute' | 'fixed';
  dropdownHeight?: number;
  disabled?: boolean;
  noScroll?: boolean;
  setIsTouched?: React.Dispatch<React.SetStateAction<boolean>>;
  setMessageState?: React.Dispatch<React.SetStateAction<boolean>>;
  closeMenu?: boolean;
  setCloseMenu?: (close: boolean) => void;
  typeName: string;
}

const InlineSelect: FCWithChildren<InlineSelectPropsInterface> = (props) => {
  const {
    selectedItemLabel,
    containerPosition,
    disabled = false,
    noScroll = false,
    dropdownHeight,
    setIsTouched,
    setMessageState,
    closeMenu,
    setCloseMenu,
    typeName,
    children,
  } = props;

  const { isWiderThanMd } = useWindowSize();

  const [showList, setShowList] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState({});

  const optionsElement = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Calculate the top position of the dropdown to be always under the label button if the position is static
   * meaning that the position of the dropdown is relative to an external parent
   */
  const updateDropDownPos = () => {
    const marginTop = isWiderThanMd ? 14 : 8;
    const pos: Pick<CSSProperties, 'height' | 'top'> =
      containerPosition === 'static' && ref?.current?.offsetTop
        ? { top: ref.current.offsetTop + ref.current.offsetHeight + marginTop }
        : {};
    if (dropdownHeight) {
      pos.height = `${dropdownHeight}px`;
    }
    setDropdownPosition(pos);
  };

  useEffect(() => {
    updateDropDownPos();
  }, [dropdownHeight, showList]);

  useEffect(() => {
    setCloseMenu(!showList);
  }, [showList]);

  useOnClickOutside(ref, () => {
    setShowList(false);
  });

  useEffect(() => {
    if (closeMenu) {
      setShowList(false);
    }
  }, [closeMenu]);

  return (
    <div
      className={clsx(
        styles.inlineSelect,
        containerPosition && styles[`inlineSelect--${containerPosition}`],
        showList && styles.active,
        disabled && styles.disabled,
      )}
      ref={ref}
    >
      <button
        data-cy={`inline-select-${typeName}`}
        ref={optionsElement}
        type="button"
        disabled={disabled}
        className={clsx(styles.inlineSelect__button, disabled && styles.disabled)}
        onClick={() => {
          if (!disabled) {
            if (setIsTouched) {
              setIsTouched(true);
            }

            if (setMessageState) {
              setMessageState(false);
            }
            setShowList(!showList);
          }
        }}
      >
        {selectedItemLabel}
        <Icon
          className={styles.inlineSelect__button__icon}
          name="ios-arrow-down-20"
        />
      </button>
      {showList && (
        <div
          className={styles.inlineSelect__dropdown}
          style={dropdownPosition}
        >
          <div
            className={clsx(
              styles.inlineSelect__dropdown__content,
              noScroll && styles['inlineSelect__dropdown__content--noScroll'],
            )}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineSelect;
